import React, { useCallback, useContext } from "react"
import { useAppDispatch } from "@/reducers/hooks"
import {
  setImageGalleryMetadataOverrides,
  showTransportBriefViewer,
  updateImageGallery,
  updatePDFViewer,
} from "@/reducers/overlaysSlice"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { Message } from "@/reducers/messagesSlice"
import { v4 as uuid } from "uuid"
import { XmppContext } from "@/stream/xmppClient"
import { AIChatMessageContext } from "./AIChatMessageContext"
import { fetchAIChatImageMetadata } from "./useAIChatImageMetadata"

const IMAGE_RE = /\.(gif|png|jpe?g|webp)(\?|#|$)/i
const PDF_RE = /\.pdf(\?|#|$)/i
/**
 * S3 URL pointing at the transport-brief digest or a per-source MD file.
 * Generated by the slickagent-pi `get_daily_transport_brief` tool.
 *
 * Shape: https://<bucket>.s3.<region>.amazonaws.com/<...>/transport-brief/<...>.md
 *
 * Matches both the bare public URL (current production after the public-read
 * bucket policy) and the legacy presigned variant with `X-Amz-Signature=` —
 * older messages in chat history may still carry signed URLs.
 *
 * The `transport-brief/` path segment + `.md` suffix uniquely identify these
 * links among any other markdown links the LLM might emit.
 */
const TRANSPORT_BRIEF_RE =
  /^https:\/\/[^/]+\.s3\.[^/]+\.amazonaws\.com\/[^?]*transport-brief\/[^?]*\.md(\?|$)/i

function isImageUrl(href: string) {
  return IMAGE_RE.test(href)
}

function isPdfUrl(href: string) {
  return PDF_RE.test(href)
}

function isTransportBriefUrl(href: string) {
  return TRANSPORT_BRIEF_RE.test(href)
}

type Props = {
  href?: string
  children?: React.ReactNode
}

function ImageGalleryLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  const dispatch = useAppDispatch()
  const { imageUrls } = useContext(AIChatMessageContext)
  const { client } = useContext(XmppContext)

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      const mappedHref = mapFileUrlToSameOrigin(href)
      const siblings = imageUrls.length > 0 ? imageUrls : [href]
      dispatch(
        updateImageGallery({
          visible: true,
          directImage: { src: mappedHref },
          directImages:
            siblings.length > 1
              ? siblings.map((src) => ({ src: mapFileUrlToSameOrigin(src) }))
              : undefined,
        }),
      )

      // Fetch metadata lazily and patch it in when it resolves.
      if (client) {
        fetchAIChatImageMetadata(client, siblings)
          .then((overrides) => {
            dispatch(setImageGalleryMetadataOverrides(overrides))
          })
          .catch((e) => {
            console.warn("[AIChatImageMetadata] fetch failed", e)
          })
      }
    },
    [dispatch, href, imageUrls, client],
  )
  return (
    <a href={mapFileUrlToSameOrigin(href)} onClick={handleClick}>
      {children}
    </a>
  )
}

function PdfViewerLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  const dispatch = useAppDispatch()
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      const mappedUrl = mapFileUrlToSameOrigin(href)
      dispatch(
        updatePDFViewer({
          visible: true,
          PDFMessage: {
            id: `ai-chat-pdf-${uuid()}`,
            body: "",
            url: mappedUrl,
            file: true,
            from: "ai-chat@local",
            to: "ai-chat@local",
            type: "groupchat",
            markers: [],
            reactions: [],
            timestamp: new Date().toISOString(),
          } as unknown as Message,
        }),
      )
    },
    [dispatch, href],
  )
  return (
    <a href={mapFileUrlToSameOrigin(href)} onClick={handleClick}>
      {children}
    </a>
  )
}

function TransportBriefLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  const dispatch = useAppDispatch()
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault()
      dispatch(showTransportBriefViewer({ url: href }))
    },
    [dispatch, href],
  )
  return (
    <a href={href} onClick={handleClick} rel="noopener noreferrer">
      {children}
    </a>
  )
}

export function AIChatLink({ href, children }: Props) {
  if (!href) {
    return <span>{children}</span>
  }
  if (isTransportBriefUrl(href)) {
    return <TransportBriefLink href={href}>{children}</TransportBriefLink>
  }
  if (isImageUrl(href)) {
    return <ImageGalleryLink href={href}>{children}</ImageGalleryLink>
  }
  if (isPdfUrl(href)) {
    return <PdfViewerLink href={href}>{children}</PdfViewerLink>
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  )
}
